import { useCallback, useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'

import {
  isConnected,
  init,
  onStatusChanged,
  getPrinters,
  printPdf,
  printRawData,
} from '../libs/jsprintmanager'

import PrinterContext from '../hooks/usePrinter/context'

const STORAGE_PREFIX = 'print-settings'

const getStoredValue = field =>
  localStorage.getItem(`${STORAGE_PREFIX}:${field}`) ?? ''

const storeValue = (field, value) =>
  localStorage.setItem(`${STORAGE_PREFIX}:${field}`, value)

export const MODEL_TYPE_NAMES = {
  single: 'SINGLE',
  multi4: 'MULTI4',
  //multi1: 'MULTI1',
  multi3: 'MULTI3',
}

export const PRINT_POSITION_TYPES = {
  askAlways: 'ask-always',
  position1: 1,
  position2: 2,
  position3: 3,
  position4: 4,
}

export const PRINT_POSITION_OPTIONS = {
  [MODEL_TYPE_NAMES.multi3]: {
    askAlways: PRINT_POSITION_TYPES.askAlways,
    position1: PRINT_POSITION_TYPES.position1,
    position2: PRINT_POSITION_TYPES.position2,
    position3: PRINT_POSITION_TYPES.position3,
  },
  [MODEL_TYPE_NAMES.multi4]: {
    askAlways: PRINT_POSITION_TYPES.askAlways,
    position1: PRINT_POSITION_TYPES.position1,
    position2: PRINT_POSITION_TYPES.position2,
    position3: PRINT_POSITION_TYPES.position3,
    position4: PRINT_POSITION_TYPES.position4,
  },
}

const MODEL_TYPES = Object.values(MODEL_TYPE_NAMES)

const PRINT_TYPES_MAP = { PDF: 'PDF', ZPL: 'ZPL', EPL: 'EPL' }
const PRINT_TYPES = Object.values(PRINT_TYPES_MAP)

const PrinterProvider = ({ children }) => {
  const [isWorking, setIsWorking] = useState(false)
  const [printers, setPrinters] = useState([])
  const [printer, setPrinter] = useState('')
  const [modelType, setModelType] = useState('')
  const [printType, setPrintType] = useState('')
  const [printPosition, setPrintPosition] = useState('')

  const loadPrinters = useCallback(async () => {
    const availablePrinters = await getPrinters()
    setPrinters(availablePrinters)
  }, [])

  const updatePrinter = useCallback(value => {
    storeValue('printer', value)
    setPrinter(value)
  }, [])

  const updatePrintPosition = useCallback(value => {
    storeValue('printPosition', value)
    setPrintPosition(value)
  }, [])

  const updateModelType = useCallback(
    value => {
      if ([MODEL_TYPE_NAMES.multi1, MODEL_TYPE_NAMES.single].includes(value)) {
        updatePrintPosition('')
      } else if (
        value === MODEL_TYPE_NAMES.multi3 &&
        printPosition ===
          PRINT_POSITION_OPTIONS[MODEL_TYPE_NAMES.multi4].position4
      ) {
        updatePrintPosition(
          PRINT_POSITION_OPTIONS[MODEL_TYPE_NAMES.multi3].askAlways,
        )
      }

      storeValue('modelType', value)
      setModelType(value)
    },
    [printPosition, updatePrintPosition],
  )

  const updatePrintType = useCallback(value => { 
    storeValue('printType', value)
    setPrintType(value)
  }, [])
 
  const printData = useCallback(
    (labelData) => {
      if (Boolean(labelData?.label)) {
        printPdf(printer, labelData?.label)  
        return
      } 
  
     printRawData(printer, labelData);

    },
    [printer],
  )

  useEffect(() => {
    init()

    onStatusChanged(() => {
      setIsWorking(isConnected())
    })
  }, [])

  useEffect(() => {
    if (isWorking) {
      loadPrinters()
    }
  }, [isWorking, loadPrinters])

  useEffect(() => {
    if (printer === '' && printers.length > 0) {
      const storedValue = getStoredValue('printer')
      if (storedValue !== '' && printers.includes(storedValue)) {
        setPrinter(storedValue)
      } else {
        updatePrinter(printers[0])
      }
    }
  }, [printer, printers, updatePrinter])

  useEffect(() => {
    if (modelType === '') {
      const storedValue = getStoredValue('modelType')
      if (storedValue !== '') {
        setModelType(storedValue)
      } else {
        updateModelType(MODEL_TYPES[0])
      }
    }
  }, [modelType, updateModelType])

  useEffect(() => {
    if (printType === '') {
      const storedValue = getStoredValue('printType')
      if (storedValue !== '') {
        setPrintType(storedValue)
      } else {
        updatePrintType(PRINT_TYPES[0])
      }
    }
  }, [printType, updatePrintType])

  useEffect(() => {
    if (printPosition === '') {
      const storedRawValue = PRINT_POSITION_TYPES.askAlways

      if (storedRawValue === '') {
        updatePrintPosition(PRINT_POSITION_TYPES.askAlways)
        return
      }

      setPrintPosition(
        storedRawValue === PRINT_POSITION_TYPES.askAlways
          ? storedRawValue
          : Number(storedRawValue),
      )
    }
  }, [printPosition, updatePrintPosition])

  const printerContext = useMemo(
    () => ({
      MODEL_TYPES,
      PRINT_TYPES,
      isWorking,
      printers,
      printer,
      modelType,
      printType,
      printPosition,
      reloadPrinters: loadPrinters,
      updatePrinter,
      updateModelType,
      updatePrintType,
      updatePrintPosition,
      printData,
    }),
    [
      isWorking,
      printers,
      printer,
      modelType,
      printType,
      printPosition,
      loadPrinters,
      updatePrinter,
      updateModelType,
      updatePrintType,
      updatePrintPosition,
      printData,
    ],
  )

  return (
    <PrinterContext.Provider value={printerContext}>
      {children}
    </PrinterContext.Provider>
  )
}

PrinterProvider.propTypes = {
  children: PropTypes.node,
}

export default PrinterProvider
